/**
 * Browser client for Neon-backed data via `/api/database`.
 */

export type DbFilter =
  | ["eq", string, unknown]
  | ["in", string, unknown[]]
  | ["not", string, "is", string | null]
  | ["is", string, string | null]
  | ["ilike", string, string];

function databaseUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const origin =
    env?.VITE_API_ORIGIN?.trim() ||
    env?.VITE_NETLIFY_FUNCTIONS_ORIGIN?.trim();
  if (origin) {
    return `${origin.replace(/\/$/, "")}/api/database`;
  }
  return "/api/database";
}

export interface DbErrorShape {
  message: string;
  code?: string;
  details?: string;
}

async function postDb(
  body: unknown,
  accessToken: string | null | undefined,
  opts?: { public?: boolean }
): Promise<{ data: any; error: DbErrorShape | null }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const res = await fetch(databaseUrl(), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  const json = (await res.json()) as {
    data: any;
    error: string | null;
    code?: string;
  };

  if (!res.ok || json.error) {
    return {
      data: null,
      error: {
        message: json.error || res.statusText,
        code: json.code,
      },
    };
  }

  return { data: json.data, error: null };
}

class TableBuilder {
  private table: string;
  /** Resolved when the query runs so callers always use the latest session token. */
  private getToken: () => string | null | undefined;
  private method: "select" | "insert" | "update" | "delete" = "select";
  private columns = "*";
  private filters: DbFilter[] = [];
  private orderSpecs: { column: string; ascending?: boolean }[] = [];
  private limitN?: number;
  private singleFlag = false;
  private maybeSingleFlag = false;
  private values?: Record<string, unknown> | Record<string, unknown>[];
  private publicMode = false;
  private embed?: ("program_ai_data")[];

  constructor(table: string, getToken: () => string | null | undefined) {
    this.table = table;
    this.getToken = getToken;
  }

  select(columns = "*") {
    this.columns = columns;
    // Supabase-compatible chaining: `.insert(...).select().single()` means
    // "return inserted rows", not "turn this into a SELECT".
    return this;
  }

  insert(values: Record<string, unknown> | Record<string, unknown>[]) {
    this.method = "insert";
    this.values = values;
    return this;
  }

  update(values: Record<string, unknown>) {
    this.method = "update";
    this.values = values;
    return this;
  }

  delete() {
    this.method = "delete";
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push(["eq", column, value]);
    return this;
  }

  in(column: string, values: unknown[]) {
    this.filters.push(["in", column, values]);
    return this;
  }

  not(column: string, op: "is", value: null) {
    this.filters.push(["not", column, "is", value]);
    return this;
  }

  is(column: string, value: null) {
    this.filters.push(["is", column, value]);
    return this;
  }

  ilike(column: string, pattern: string) {
    this.filters.push(["ilike", column, pattern]);
    return this;
  }

  or(_expression: string) {
    // The database BFF does not implement Supabase's `or` filter yet.
    // Keeping this no-op preserves legacy callers that used it for optional
    // broadening while the server-side scoping still protects access.
    return this;
  }

  order(column: string, opts?: { ascending?: boolean }) {
    this.orderSpecs.push({ column, ascending: opts?.ascending });
    return this;
  }

  limit(n: number) {
    this.limitN = n;
    return this;
  }

  single() {
    this.singleFlag = true;
    this.maybeSingleFlag = false;
    return this;
  }

  maybeSingle() {
    this.maybeSingleFlag = true;
    this.singleFlag = false;
    return this;
  }

  /** Published-content reads without a session */
  asPublic() {
    this.publicMode = true;
    return this;
  }

  withEmbed(...parts: ("program_ai_data")[]) {
    this.embed = parts;
    return this;
  }

  private buildBody(): unknown {
    return {
      op: "from",
      method: this.method,
      table: this.table,
      columns: this.columns,
      values: this.values,
      filters: this.filters,
      order: this.orderSpecs.length ? this.orderSpecs : undefined,
      limit: this.limitN,
      single: this.singleFlag || undefined,
      maybeSingle: this.maybeSingleFlag || undefined,
      public: this.publicMode || undefined,
      embed: this.embed?.length ? this.embed : undefined,
    };
  }

  then<TResult1 = { data: any; error: DbErrorShape | null }, TResult2 = never>(
    onfulfilled?: ((value: { data: any; error: DbErrorShape | null }) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return postDb(this.buildBody(), this.publicMode ? null : this.getToken(), {
      public: this.publicMode,
    }).then(onfulfilled as (v: unknown) => TResult1 | PromiseLike<TResult1>, onrejected as (e: unknown) => TResult2 | PromiseLike<TResult2>);
  }
}

export class NeonDbClient {
  constructor(private getAccessToken: () => string | null | undefined) {}

  from(table: string) {
    return new TableBuilder(table, () => this.getAccessToken());
  }

  async rpc<T = any>(
    fn: string,
    args?: Record<string, unknown>
  ): Promise<{ data: T | null; error: DbErrorShape | null }> {
    return postDb(
      { op: "rpc", name: fn, args: args ?? {} },
      this.getAccessToken()
    ) as Promise<{ data: T | null; error: DbErrorShape | null }>;
  }
}
